import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { APP_ROUTES } from '@taxora-business-core/util/constants/app-routes.constant';
import {
  SetAccessToken,
  SetCurrentUser,
} from '@taxora-business-core/data-access/state/app.actions';
import {
  activateUser,
  Api,
  createUser,
  forgotPassword,
  resendActivation,
  resetPassword,
  signIn,
} from '@taxora-api';
import {
  ActivateUser,
  CreateUser,
  ForgotPassword,
  ResendActivation,
  SetPassword,
  SignIn,
} from './auth-actions';
import { firstValueFrom } from 'rxjs';

export interface AuthStateModel {
  isCreateUserLoading: boolean;
  isSignInLoading: boolean;
  createUserEmail: string | undefined;
  isActivateUserLoading: boolean;
  isResendActivationLoading: boolean;
  resendActivationEmail: string | undefined;
  isForgotPasswordLoading: boolean;
  forgotPasswordEmail: string | undefined;
  isSetPasswordLoading: boolean;
}

@State<AuthStateModel>({
  name: 'auth',
  defaults: {
    isCreateUserLoading: false,
    isSignInLoading: false,
    createUserEmail: undefined,
    isActivateUserLoading: false,
    isResendActivationLoading: false,
    isForgotPasswordLoading: false,
    forgotPasswordEmail: undefined,
    isSetPasswordLoading: false,
    resendActivationEmail: undefined,
  },
})
@Injectable()
export class AuthState {
  private api = inject(Api);
  private router = inject(Router);

  @Selector()
  static isCreateUserLoading(state: AuthStateModel): boolean {
    return state.isCreateUserLoading;
  }

  @Selector()
  static isSignInLoading(state: AuthStateModel): boolean {
    return state.isSignInLoading;
  }

  @Selector()
  static createUserEmail(state: AuthStateModel): string | undefined {
    return state.createUserEmail;
  }

  @Selector()
  static isActivateUserLoading(state: AuthStateModel): boolean {
    return state.isActivateUserLoading;
  }

  @Selector()
  static isResendActivationLoading(state: AuthStateModel): boolean {
    return state.isResendActivationLoading;
  }

  @Selector()
  static resendActivationEmail(state: AuthStateModel): string | undefined {
    return state.resendActivationEmail;
  }

  @Selector()
  static isForgotPasswordLoading(state: AuthStateModel): boolean {
    return state.isForgotPasswordLoading;
  }

  @Selector()
  static forgotPasswordEmail(state: AuthStateModel): string | undefined {
    return state.forgotPasswordEmail;
  }

  @Selector()
  static isSetPasswordLoading(state: AuthStateModel): boolean {
    return state.isSetPasswordLoading;
  }

  @Action(CreateUser)
  async createUser(
    { patchState }: StateContext<AuthStateModel>,
    { data }: CreateUser,
  ): Promise<void> {
    patchState({ isCreateUserLoading: true });
    await this.api
      .invoke(createUser, {
        body: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          password: data.password,
          role: 'SUPER_ADMIN',
        },
      })
      .then((resp) => {
        patchState({ createUserEmail: resp.email });
        this.router.navigate([
          '/',
          APP_ROUTES.AUTH.ROOT,
          APP_ROUTES.AUTH.REGISTER.ROOT,
          APP_ROUTES.AUTH.REGISTER.SUCCESS,
        ]);
      })
      .finally(() => patchState({ isCreateUserLoading: false }));
  }

  @Action(ActivateUser)
  async activateUser({ patchState }: StateContext<AuthStateModel>, { token }: ActivateUser) {
    patchState({ isActivateUserLoading: true });

    await this.api
      .invoke(activateUser, { body: { token } })
      .then(() => {
        patchState({ isActivateUserLoading: false });
        this.router.navigate([
          '/',
          APP_ROUTES.AUTH.ROOT,
          APP_ROUTES.AUTH.ACTIVATE.ROOT,
          APP_ROUTES.AUTH.ACTIVATE.SUCCESS,
        ]);
      })
      .catch(() => {
        this.router.navigate(['/']);
        patchState({ isActivateUserLoading: false });
      });
  }

  @Action(ResendActivation)
  async resendActivation({ patchState }: StateContext<AuthStateModel>, { data }: ResendActivation) {
    patchState({ isResendActivationLoading: true });

    await this.api
      .invoke(resendActivation, { body: { email: data.email } })
      .then(() => {
        patchState({
          resendActivationEmail: data.email,
        });
        this.router.navigate([
          '/',
          APP_ROUTES.AUTH.ROOT,
          APP_ROUTES.AUTH.RESEND_ACTIVATION.ROOT,
          APP_ROUTES.AUTH.RESEND_ACTIVATION.SUCCESS,
        ]);
      })
      .finally(() => patchState({ isResendActivationLoading: false }));
  }

  @Action(SignIn)
  async signIn(
    { patchState, dispatch }: StateContext<AuthStateModel>,
    { data, returnUrl }: SignIn,
  ) {
    patchState({ isSignInLoading: true });

    await this.api
      .invoke(signIn, {
        body: {
          email: data.email,
          password: data.password,
        },
      })
      .then(async (resp) => {
        await firstValueFrom(
          dispatch([new SetAccessToken(resp.accessToken), new SetCurrentUser(resp.user)]),
        );
        if (returnUrl?.startsWith('/')) {
          this.router.navigateByUrl(returnUrl);
        } else {
          this.router.navigate([APP_ROUTES.DASHBOARD]);
        }
      })
      .finally(() => patchState({ isSignInLoading: false }));
  }

  @Action(ForgotPassword)
  async forgotPassword({ patchState }: StateContext<AuthStateModel>, { data }: ForgotPassword) {
    patchState({ isForgotPasswordLoading: true });

    await this.api
      .invoke(forgotPassword, { body: { email: data.email } })
      .then(() => {
        patchState({
          forgotPasswordEmail: data.email,
        });
        this.router.navigate([
          '/',
          APP_ROUTES.AUTH.ROOT,
          APP_ROUTES.AUTH.FORGOT_PASSWORD.ROOT,
          APP_ROUTES.AUTH.FORGOT_PASSWORD.SUCCESS,
        ]);
      })
      .finally(() => patchState({ isForgotPasswordLoading: false }));
  }

  @Action(SetPassword)
  async setPassword({ patchState }: StateContext<AuthStateModel>, { token, data }: SetPassword) {
    patchState({ isSetPasswordLoading: true });

    await this.api
      .invoke(resetPassword, { body: { token, newPassword: data.password } })
      .then(() => {
        patchState({ isSetPasswordLoading: false });
        this.router.navigate([
          '/',
          APP_ROUTES.AUTH.ROOT,
          APP_ROUTES.AUTH.SET_PASSWORD.ROOT,
          APP_ROUTES.AUTH.SET_PASSWORD.SUCCESS,
        ]);
      })
      .catch(() => {
        patchState({ isSetPasswordLoading: false });
      });
  }
}
